import { AxiosInstance } from "axios";
import { createApi } from "../../utils/createApi";
import { abortableWait } from "../../utils/abortableWait";

/**
 * Genetec "send detections to Security Center as alarms" (ENG-2219).
 *
 * actuate_admin:
 * - GET  customer/{id}/genetec_alarm_setup/ → what would be created + how many
 *   streams already alarm.
 * - POST customer/{id}/genetec_alarm_setup/ → dispatches the
 *   `configure-genetec-alarms` Command and turns the alarm on for the site's
 *   streams; 400 when the site isn't Genetec or has no alarm names.
 * - The outcome is read from the same action_status endpoint the other site
 *   menu actions poll (`?action=genetec_alarm_setup`).
 */

export type GenetecAlarmCommandStatus =
  | "pending"
  | "success"
  | "partial"
  | "failed"
  | "timed_out";

export interface GenetecAlarmOutcome {
  name: string;
  status: string;
  detail?: string | null;
  guid?: string | null;
}

export interface GenetecAlarmResult {
  alarms?: GenetecAlarmOutcome[];
  needs_recipients?: string[];
  error?: string | null;
}

export interface GenetecAlarmLastResult {
  status: GenetecAlarmCommandStatus | null;
  result: GenetecAlarmResult | null;
  [key: string]: unknown;
}

export interface GenetecAlarmSetup {
  alarm_names: string[];
  streams_total: number;
  streams_alarm_on: number;
  is_genetec: boolean;
  last_result: GenetecAlarmLastResult | null;
}

export interface GenetecAlarmSetupDispatch {
  command_id: number;
  alarm_names: string[];
  streams_updated: number;
}

// GET customer/{id}/action_status/?action=genetec_alarm_setup
export interface GenetecAlarmActionStatus {
  id: number;
  action: string;
  status: GenetecAlarmCommandStatus | null;
  result: GenetecAlarmResult | null;
  request_date: string;
  completed_at: string | null;
}

export const GENETEC_ALARM_ACTION = "genetec_alarm_setup";
export const GENETEC_ALARM_POLL_INTERVAL_MS = 3000;
export const GENETEC_ALARM_POLL_ATTEMPTS = 40; // ~2 minutes

/**
 * The onboarding prompt only shows when there is something to do: a Genetec
 * site, at least one alarm to create, and at least one stream not yet sending
 * alarms. (The site-settings entry point opens regardless; re-running is safe.)
 */
export const shouldPromptGenetecAlarmSetup = (
  setup: GenetecAlarmSetup | null | undefined
): boolean =>
  Boolean(
    setup &&
      setup.is_genetec &&
      Array.isArray(setup.alarm_names) &&
      setup.alarm_names.length > 0 &&
      setup.streams_alarm_on < setup.streams_total
  );

const api = (): AxiosInstance => createApi(``);

export const fetchGenetecAlarmSetup = async (
  siteId: string | number,
  signal?: AbortSignal
): Promise<GenetecAlarmSetup> => {
  const response = await api().get<GenetecAlarmSetup>(
    `customer/${siteId}/genetec_alarm_setup/`,
    { signal }
  );
  return response.data;
};

export const startGenetecAlarmSetup = async (
  siteId: string | number
): Promise<GenetecAlarmSetupDispatch> => {
  const response = await api().post<GenetecAlarmSetupDispatch>(
    `customer/${siteId}/genetec_alarm_setup/`
  );
  return response.data;
};

export interface GenetecAlarmPollOptions {
  attempts?: number;
  intervalMs?: number;
  // Aborting stops the poll at the next step; the caller aborts on unmount.
  signal?: AbortSignal;
}

/**
 * Polls action_status until the Command the POST created (matched by id, so a
 * row from an earlier run never counts) leaves `pending`. A 404 (row not
 * visible yet) or transient error is retried like a pending row.
 *
 * Returns the finished row, `"timeout"` when the attempts run out first, or
 * null when the signal aborts.
 */
export const pollGenetecAlarmSetup = async (
  siteId: string | number,
  commandId: number,
  options: GenetecAlarmPollOptions = {}
): Promise<GenetecAlarmActionStatus | "timeout" | null> => {
  const attempts = options.attempts ?? GENETEC_ALARM_POLL_ATTEMPTS;
  const intervalMs = options.intervalMs ?? GENETEC_ALARM_POLL_INTERVAL_MS;
  const signal = options.signal;
  const client = api();

  for (let attempt = 0; attempt < attempts; attempt++) {
    await abortableWait(intervalMs, signal);
    if (signal?.aborted) return null;
    try {
      const response = await client.get<GenetecAlarmActionStatus>(
        `customer/${siteId}/action_status/?action=${GENETEC_ALARM_ACTION}`,
        { signal }
      );
      if (signal?.aborted) return null;
      const data = response.data;
      if (
        data &&
        data.id === commandId &&
        data.status &&
        data.status !== "pending"
      ) {
        return data;
      }
    } catch {
      // 404 (no row yet), transient error, or aborted: the loop decides.
    }
  }
  return signal?.aborted ? null : "timeout";
};
